import { apiClient, withConflictRetry } from './api';
import {
  Availability,
  AddAvailabilityRequest,
  UpdateAvailabilityRequest,
  UpdateAvailabilityTimesRequest,
} from '../types/availability';

export const availabilityService = {
  // Get all availabilities for an apartment
  async getAvailabilities(apartmentId: string): Promise<Availability[]> {
    const response = await apiClient.get<Availability[]>(
      `/apartments/${apartmentId}/availabilities`
    );
    return response.data;
  },

  // Get only available slots
  async getAvailableSlots(apartmentId: string): Promise<Availability[]> {
    const response = await apiClient.get<Availability[]>(
      `/apartments/${apartmentId}/availabilities/available`
    );
    return response.data;
  },

  // Add new availability period — retries on 409
  async addAvailability(
    apartmentId: string,
    data: AddAvailabilityRequest
  ): Promise<Availability> {
    return withConflictRetry(
      () =>
        apiClient
          .post<Availability>(`/apartments/${apartmentId}/availabilities`, data)
          .then(r => r.data),
      () => this.getAvailabilities(apartmentId).then(list => list[list.length - 1])
    );
  },

  // Update existing availability — retries on 409
  async updateAvailability(
    apartmentId: string,
    availabilityId: string,
    data: UpdateAvailabilityRequest
  ): Promise<Availability> {
    return withConflictRetry(
      () =>
        apiClient
          .put<Availability>(
            `/apartments/${apartmentId}/availabilities/${availabilityId}`,
            data
          )
          .then(r => r.data),
      () =>
        this.getAvailabilities(apartmentId).then(
          list => list.find(a => a.id === availabilityId) as Availability
        )
    );
  },

  // Delete availability (no optimistic-lock risk — no retry needed)
  async removeAvailability(
    apartmentId: string,
    availabilityId: string
  ): Promise<void> {
    await apiClient.delete(
      `/apartments/${apartmentId}/availabilities/${availabilityId}`
    );
  },

  // Block availability — retries on 409
  async blockAvailability(
    apartmentId: string,
    availabilityId: string
  ): Promise<Availability> {
    return withConflictRetry(
      () =>
        apiClient
          .post<Availability>(
            `/apartments/${apartmentId}/availabilities/${availabilityId}/block`
          )
          .then(r => r.data),
      () =>
        this.getAvailabilities(apartmentId).then(
          list => list.find(a => a.id === availabilityId) as Availability
        )
    );
  },

  // Unblock availability — retries on 409
  async unblockAvailability(
    apartmentId: string,
    availabilityId: string
  ): Promise<Availability> {
    return withConflictRetry(
      () =>
        apiClient
          .post<Availability>(
            `/apartments/${apartmentId}/availabilities/${availabilityId}/unblock`
          )
          .then(r => r.data),
      () =>
        this.getAvailabilities(apartmentId).then(
          list => list.find(a => a.id === availabilityId) as Availability
        )
    );
  },

  // ── 022-checkin-checkout-times ────────────────────────────────────────────

  /**
   * PATCH /apartments/{apartmentId}/availabilities/{availabilityId}/times
   * Updates default check-in/out times and auto flags for an availability period.
   * Uses withConflictRetry — the availability record uses optimistic locking.
   * Does NOT affect already-created bookings (backend invariant).
   */
  async updateAvailabilityTimes(
    apartmentId: string,
    availabilityId: string,
    data: UpdateAvailabilityTimesRequest
  ): Promise<Availability> {
    return withConflictRetry(
      () =>
        apiClient
          .patch<Availability>(
            `/apartments/${apartmentId}/availabilities/${availabilityId}/times`,
            data
          )
          .then(r => r.data),
      () =>
        this.getAvailabilities(apartmentId).then(
          list => list.find(a => a.id === availabilityId) as Availability
        )
    );
  },

  // ── end 022-checkin-checkout-times ─────────────────────────────────────────
};
